import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, SIZES, fmt, fmtInt, formatApiError } from "@/lib/api";
import { Printer, Download, ArrowLeft, IndianRupee, X, Pencil, Building2, MapPin } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

const STATUS_ACCENT = {
  DRAFT: "#F59E0B",
  BALANCE_PENDING: "#DC2626",
  CLEARED: "#16A34A",
  CANCELLED: "#94A3B8",
};

export default function InvoiceView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState({});
  const [payOpen, setPayOpen] = useState(false);

  const load = () => api.get(`/invoices/${id}`).then(({ data }) => setInv(data));
  useEffect(() => {
    load();
    api.get("/dashboard").then(({ data }) => setCompany(data.company || {}));
  }, [id]);

  if (!inv) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading invoice...</div>;

  const deletePayment = async (payId) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await api.delete(`/invoices/${id}/payments/${payId}`);
      await load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const downloadPdf = async () => {
    const resp = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const balance = inv.balance ?? Math.max(0, (inv.grand_total || 0) - (inv.amount_received || 0));
  const accent = STATUS_ACCENT[inv.status || "DRAFT"] || "#94A3B8";
  const canEdit = inv.status === "DRAFT" || inv.status === "BALANCE_PENDING";
  const totalQty = (inv.items || []).reduce((a, it) => a + Number(it.qty || 0), 0);
  const hasReturnItems = (inv.return_items || []).length > 0;
  const returnTotalQty = (inv.return_items || []).reduce((a, it) => a + Number(it.qty || 0), 0);

  return (
    <div data-testid="invoice-view-page">
      {/* ── Header / actions ──────────────────────────────────────── */}
      <div className="page-header no-print" style={{ marginBottom: 22 }}>
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/invoices")} style={{ marginBottom: 8, paddingLeft: 0 }}>
            <ArrowLeft size={14}/> Back to Invoices
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className="page-title" style={{ letterSpacing: -0.5 }}>{inv.invoice_number}</h1>
            <StatusBadge status={inv.status || "DRAFT"}/>
          </div>
          <p className="page-sub" style={{ marginTop: 4 }}>Created on {inv.invoice_date}</p>
        </div>
        <div className="toolbar" style={{ gap: 8 }}>
          {canEdit && (
            <button className="btn btn-secondary" data-testid="edit-invoice-btn" onClick={() => nav(`/invoices/${inv.id}/edit`)}>
              <Pencil size={16}/> Edit Invoice
            </button>
          )}
          <button className="btn btn-secondary" data-testid="update-payment-btn" onClick={() => setPayOpen(true)} disabled={inv.status === "CANCELLED"} title={inv.status === "CANCELLED" ? "Invoice is cancelled" : ""}>
            <IndianRupee size={16}/> Add Payment
          </button>
          <button className="btn btn-secondary" data-testid="print-invoice" onClick={() => window.print()}><Printer size={16}/> Print</button>
          <button className="btn btn-primary" data-testid="download-pdf" onClick={downloadPdf}><Download size={16}/> Download PDF</button>
        </div>
      </div>

      {/* ── Status banners ────────────────────────────────────────── */}
      {balance > 0 && inv.status !== "CANCELLED" && (
        <div className="no-print balance-highlight" data-testid="balance-banner" style={{ marginBottom: 18, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Balance Pending</span>
          <strong className="tabular" style={{ fontSize: 17 }}>₹ {fmt(balance)} <span style={{ fontWeight: 400, opacity: 0.7, fontSize: 13 }}>of ₹ {fmtInt(Math.round(inv.grand_total || 0))}</span></strong>
        </div>
      )}
      {inv.status === "CANCELLED" && (
        <div className="no-print" data-testid="cancelled-banner" style={{ marginBottom: 18, fontSize: 14, padding: "14px 18px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, color: "#475569" }}>
          <strong style={{ color: "#1F2937" }}>This invoice has been cancelled.</strong>&nbsp;Inventory has been restored. Payment records below are retained for history.
        </div>
      )}

      {/* ── Invoice document ──────────────────────────────────────── */}
      <div className="card" data-testid="invoice-detail" style={{ padding: 0, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)" }}>
        <div style={{ height: 4, background: accent }}/>

        <div style={{ padding: "36px 40px" }}>
          {/* Company + invoice meta */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 30, gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Building2 size={20} color="white"/>
              </div>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.3 }}>{company.name}</h2>
                <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7 }}>
                  {company.office_address && <div><span style={{ color: "#94A3B8" }}>Office —</span> {company.office_address}</div>}
                  {company.factory_address && <div><span style={{ color: "#94A3B8" }}>Factory —</span> {company.factory_address}</div>}
                  {company.phone && <div><span style={{ color: "#94A3B8" }}>Phone —</span> {company.phone}</div>}
                  {company.gst && <div><span style={{ color: "#94A3B8" }}>GST —</span> {company.gst}</div>}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600 }}>Tax Invoice</div>
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "Outfit", marginTop: 4, letterSpacing: -0.5 }}>{inv.invoice_number}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6 }}>Date: {inv.invoice_date}</div>
            </div>
          </div>

          {/* Bill To */}
          <div style={{ background: "#F8FAFC", border: "1px solid #EEF2F7", borderRadius: 10, padding: "18px 20px", marginBottom: 30 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 8 }}>
              <MapPin size={12}/> Bill To
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{inv.customer_snapshot?.name}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3, lineHeight: 1.6 }}>{inv.shipping_address || inv.customer_snapshot?.address}</div>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.8 }}>
                <div><span style={{ color: "#94A3B8" }}>Phone —</span> {inv.customer_snapshot?.phone || "—"}</div>
                <div><span style={{ color: "#94A3B8" }}>GST —</span> {inv.gst_number || "—"}</div>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div style={{ marginBottom: 8, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Items
          </div>
          <table className="tbl" style={{ marginBottom: 26, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
            <thead><tr><th>Style</th><th>Product</th><th>Color</th>{SIZES.map((s) => <th key={s} className="num">{s}</th>)}<th className="num">Qty</th><th className="num">Price</th><th className="num">Line Total</th></tr></thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} style={{ background: i % 2 === 1 ? "#FBFCFD" : "white" }}>
                  <td><strong>{it.style_id}</strong></td>
                  <td>{it.product_name}</td>
                  <td>{it.color}</td>
                  {SIZES.map((s) => <td key={s} className="num">{it.sizes[s] || 0}</td>)}
                  <td className="num">{it.qty}</td>
                  <td className="num">₹ {fmt(it.unit_price)}</td>
                  <td className="num"><strong>₹ {fmt(it.line_total)}</strong></td>
                </tr>
              ))}
              <tr style={{ background: "#F8FAFC" }}>
                <td colSpan={3 + SIZES.length} style={{ textAlign: "right", fontWeight: 600 }}>Total Qty</td>
                <td className="num tabular" data-testid="invoice-total-qty"><strong>{totalQty}</strong></td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>

          {/* Return Items */}
          {(inv.return_items && inv.return_items.length > 0) && (
            <div data-testid="invoice-return-items" style={{ marginBottom: 26 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--danger, #DC2626)", marginBottom: 8 }}>Return Items</div>
              <table className="tbl" style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #FCA5A5" }}>
                <thead style={{ background: "#FEE2E2" }}>
                  <tr>
                    <th style={{ color: "#991B1B" }}>Style</th><th style={{ color: "#991B1B" }}>Product</th><th style={{ color: "#991B1B" }}>Color</th>
                    {SIZES.map((s) => <th key={s} className="num" style={{ color: "#991B1B" }}>{s}</th>)}
                    <th className="num" style={{ color: "#991B1B" }}>Qty</th><th className="num" style={{ color: "#991B1B" }}>Price</th><th className="num" style={{ color: "#991B1B" }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.return_items.map((it, i) => (
                    <tr key={i} style={{ background: i % 2 === 1 ? "#FFF5F5" : "white", color: "var(--danger, #DC2626)" }}>
                      <td><strong>{it.style_id}</strong></td>
                      <td>{it.product_name}</td>
                      <td>{it.color}</td>
                      {SIZES.map((s) => <td key={s} className="num">{it.sizes[s] || 0}</td>)}
                      <td className="num">{it.qty}</td>
                      <td className="num">₹ {fmt(it.unit_price)}</td>
                      <td className="num"><strong>− ₹ {fmt(it.line_total)}</strong></td>
                    </tr>
                  ))}
                  <tr style={{ background: "#FEE2E2" }}>
                    <td colSpan={3 + SIZES.length} style={{ textAlign: "right", fontWeight: 600, color: "#991B1B" }}>Total Return Qty</td>
                    <td className="num tabular" data-testid="invoice-return-total-qty" style={{ color: "#991B1B" }}><strong>{returnTotalQty}</strong></td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <div style={{ width: 380, background: "#F8FAFC", border: "1px solid #EEF2F7", borderRadius: 10, padding: 20 }}>
              <Row label="Subtotal" value={`₹ ${fmt(inv.subtotal)}`}/>
              {(inv.discount || 0) > 0 && <Row label="Discount" value={`- ₹ ${fmt(inv.discount)}`}/>}
              {(inv.cgst_amount || 0) > 0 && <Row label={`CGST (${inv.cgst_pct}%)`} value={`+ ₹ ${fmt(inv.cgst_amount)}`}/>}
              {(inv.sgst_amount || 0) > 0 && <Row label={`SGST (${inv.sgst_pct}%)`} value={`+ ₹ ${fmt(inv.sgst_amount)}`}/>}
              {(inv.igst_amount || 0) > 0 && <Row label={`IGST (${inv.igst_pct}%)`} value={`+ ₹ ${fmt(inv.igst_amount)}`}/>}
              {(inv.shipping_charges || 0) > 0 && <Row label="Shipping" value={`+ ₹ ${fmt(inv.shipping_charges)}`}/>}
              {hasReturnItems && (
                <div style={{ borderTop: "1px dashed #E2E8F0", marginTop: 10, paddingTop: 10 }}>
                  <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Sale Total</span>} value={`₹ ${fmt(inv.sale_total ?? inv.grand_total)}`}/>
                  <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Return Subtotal</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(inv.return_subtotal)}</span>}/>
                  {(inv.return_cgst_amount || 0) > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return CGST (${inv.return_cgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(inv.return_cgst_amount)}</span>}/>}
                  {(inv.return_sgst_amount || 0) > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return SGST (${inv.return_sgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(inv.return_sgst_amount)}</span>}/>}
                  {(inv.return_igst_amount || 0) > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return IGST (${inv.return_igst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(inv.return_igst_amount)}</span>}/>}
                </div>
              )}
              <div style={{ borderTop: "1px solid #E2E8F0", marginTop: 10, paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Grand Total</span>
                <span className="tabular" style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>₹ {fmtInt(Math.round(inv.grand_total || 0))}</span>
              </div>
              {(inv.amount_received || 0) > 0 && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed #E2E8F0" }}>
                  <Row label="Total Paid" value={`₹ ${fmt(inv.amount_received)}`}/>
                  <Row
                    label={<strong style={{ color: balance > 0 ? "var(--danger)" : "var(--success)" }}>Balance Due</strong>}
                    value={<strong style={{ color: balance > 0 ? "var(--danger)" : "var(--success)" }} className="tabular">₹ {fmt(balance)}</strong>}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Payment History */}
          {(inv.payments && inv.payments.length > 0) && (
            <div data-testid="payments-history" style={{ marginTop: 34 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 10 }}>
                Payment Records ({inv.payments.length})
              </div>
              <table className="tbl" style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                <thead>
                  <tr><th style={{ width: 50 }}>#</th><th>Date</th><th>Method</th><th className="num">Amount</th><th className="no-print" style={{ width: 60 }}></th></tr>
                </thead>
                <tbody>
                  {inv.payments.map((p, i) => (
                    <tr key={p.id || i} data-testid={`payment-row-${i}`} style={{ background: i % 2 === 1 ? "#FBFCFD" : "white" }}>
                      <td>{i + 1}</td>
                      <td>{p.date}</td>
                      <td><span className="badge badge-info">{(p.method || "").toUpperCase()}</span></td>
                      <td className="num tabular"><strong>₹ {fmt(p.amount)}</strong></td>
                      <td className="no-print">
                        <button className="btn btn-ghost btn-sm" data-testid={`delete-payment-${i}`} title="Delete payment" onClick={() => deletePayment(p.id)}><X size={14}/></button>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: "#F8FAFC" }}>
                    <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>Total Received</td>
                    <td className="num tabular" data-testid="payments-total"><strong>₹ {fmt(inv.amount_received)}</strong></td>
                    <td className="no-print"></td>
                  </tr>
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right", fontWeight: 600, color: balance > 0 ? "var(--danger)" : "var(--success)" }}>Balance</td>
                    <td className="num tabular" data-testid="payments-balance" style={{ color: balance > 0 ? "var(--danger)" : "var(--success)", fontWeight: 700 }}>₹ {fmt(balance)}</td>
                    <td className="no-print"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p style={{ marginTop: 38, color: "var(--text-muted)", fontSize: 12.5, borderTop: "1px solid #F1F5F9", paddingTop: 18 }}>
            Thank you for your business.
          </p>
        </div>
      </div>

      {payOpen && <PaymentDialog inv={inv} onClose={() => setPayOpen(false)} onSaved={async () => { setPayOpen(false); await load(); }}/>}
    </div>
  );
}

function Row({ label, value }) {
  return <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 14 }}><span style={{ color: "var(--text-muted)" }}>{label}</span><span className="tabular">{value}</span></div>;
}

function PaymentDialog({ inv, onClose, onSaved }) {
  const [pm, setPm] = useState("cash");
  const totalPaid = Number(inv.amount_received || 0);
  const remaining = Math.max(0, (inv.grand_total || 0) - totalPaid);
  const [amt, setAmt] = useState(remaining);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const newTotal = totalPaid + Number(amt || 0);
  const newBalance = Math.max(0, (inv.grand_total || 0) - newTotal);
  const newStatus = (() => {
    if (newTotal <= 0) return "DRAFT";
    if (newTotal + 0.01 >= (inv.grand_total || 0)) return "CLEARED";
    return "BALANCE_PENDING";
  })();

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/invoices/${inv.id}/payment`, { payment_method: pm, amount_received: Number(amt) || 0 });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="payment-modal" style={{ maxWidth: 480 }}>
        <div className="modal-h"><h2>Add Payment</h2><button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button></div>
        <div className="modal-b">
          {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14, padding: 12, background: "#F8FAFC", borderRadius: 8, border: "1px solid var(--border)" }}>
            <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>Grand Total</div><div className="tabular" style={{ fontWeight: 600 }}>₹ {fmtInt(Math.round(inv.grand_total || 0))}</div></div>
            <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>Already Paid</div><div className="tabular" style={{ fontWeight: 600 }}>₹ {fmt(totalPaid)}</div></div>
            <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>Remaining Balance</div><div className="tabular" style={{ fontWeight: 600, color: remaining > 0 ? "var(--danger)" : "var(--success)" }}>₹ {fmt(remaining)}</div></div>
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label>Payment Method *</label>
            <select data-testid="pay-method" className="select" value={pm} onChange={(e) => setPm(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="neft">NEFT</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label>This Payment Amount (₹) *</label>
            <input data-testid="pay-amount" className="input num" type="number" step="0.01" min="0.01" value={amt} onChange={(e) => setAmt(e.target.value)}/>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px dashed var(--border)" }}>
            <span style={{ color: "var(--text-muted)" }}>New Total Paid</span>
            <span className="tabular" style={{ fontWeight: 600 }}>₹ {fmt(newTotal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "var(--text-muted)" }}>New Balance</span>
            <span className="tabular" style={{ fontWeight: 700, color: newBalance > 0 ? "var(--danger)" : "var(--success)" }}>₹ {fmt(newBalance)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={{ color: "var(--text-muted)" }}>New Status</span>
            <StatusBadge status={newStatus}/>
          </div>
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="pay-save" disabled={busy || Number(amt) <= 0} onClick={save}>{busy ? "Saving..." : "Add Payment"}</button>
        </div>
      </div>
    </div>
  );
}